/**
 * Typed pub/sub event bus for cross-module communication.
 *
 * Modules communicate via the event bus rather than importing each other's
 * stores directly. This keeps modules decoupled and allows integration
 * points to be toggled on/off by subscribing/unsubscribing listeners.
 */

import type { EventMap } from '../types/modules';

/** Callback type for an event listener. */
type Listener<T> = (payload: T) => void;

/** The event bus interface returned by createEventBus. */
export interface EventBus<T extends Record<string, unknown>> {
  /** Register a listener for an event. Returns an unsubscribe function. */
  subscribe<K extends keyof T>(event: K, callback: Listener<T[K]>): () => void;
  /** Remove a specific listener for an event. */
  unsubscribe<K extends keyof T>(event: K, callback: Listener<T[K]>): void;
  /** Emit an event to all registered listeners. */
  emit<K extends keyof T>(event: K, payload: T[K]): void;
  /** Remove all listeners (useful for testing). */
  clear(): void;
}

/**
 * Creates a type-safe event bus.
 *
 * The event name constrains the payload type via the generic EventMap.
 * Supports multiple listeners per event and handles listener errors
 * gracefully (catch and log, don't break other listeners).
 */
export function createEventBus<T extends Record<string, unknown>>(): EventBus<T> {
  const listeners = new Map<keyof T, Set<Listener<unknown>>>();

  function getListeners<K extends keyof T>(event: K): Set<Listener<unknown>> {
    if (!listeners.has(event)) {
      listeners.set(event, new Set());
    }
    return listeners.get(event)!;
  }

  function subscribe<K extends keyof T>(event: K, callback: Listener<T[K]>): () => void {
    const set = getListeners(event);
    set.add(callback as Listener<unknown>);
    return () => unsubscribe(event, callback);
  }

  function unsubscribe<K extends keyof T>(event: K, callback: Listener<T[K]>): void {
    const set = listeners.get(event);
    if (set) {
      set.delete(callback as Listener<unknown>);
    }
  }

  function emit<K extends keyof T>(event: K, payload: T[K]): void {
    const set = listeners.get(event);
    if (!set) return;
    for (const callback of set) {
      try {
        callback(payload);
      } catch (error) {
        console.error(`[EventBus] Error in listener for event "${String(event)}":`, error);
      }
    }
  }

  function clear(): void {
    listeners.clear();
  }

  return { subscribe, unsubscribe, emit, clear };
}

/** Singleton event bus instance for the Kira app. */
export const eventBus = createEventBus<EventMap>();
